import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CountryFlag, MultiSelectFilter, SimpleSelect } from '@/shared/ui';
import {
  PROVIDER_STATUS_I18N_KEY,
  useProviderOptions,
} from '@entities/Provider';
import type { AvailabilityStatus } from '@psp-aggregator/sdk';

export interface ProviderFilterValues {
  countries: string[];
  currencies: string[];
  industries: string[];
  integrations: string[];
  paymentMethods: string[];
  status: AvailabilityStatus | '';
}

export const EMPTY_PROVIDER_FILTERS: ProviderFilterValues = {
  countries: [],
  currencies: [],
  industries: [],
  integrations: [],
  paymentMethods: [],
  status: '',
};

export interface ProviderFiltersProps {
  onChange: (values: ProviderFilterValues) => void;
  statusOptions: readonly AvailabilityStatus[];
  values: ProviderFilterValues;
}

const named = (
  options: readonly { code: string; name: string }[] | undefined,
) =>
  (options ?? []).map((option) => ({
    label: option.name,
    value: option.code,
  }));

export const ProviderFilters: FC<ProviderFiltersProps> = ({
  onChange,
  statusOptions,
  values,
}) => {
  const { t } = useTranslation();
  const optionsQuery = useProviderOptions();
  const options = optionsQuery.data;
  const countries = useMemo(
    () =>
      (options?.countries ?? []).map((country) => ({
        icon: <CountryFlag code={country.code} />,
        label: country.name,
        value: country.code,
      })),
    [options?.countries],
  );
  const patch = (next: Partial<ProviderFilterValues>): void => {
    onChange({ ...values, ...next });
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <MultiSelectFilter
        label={t('pages.admin.providers.filters.country')}
        options={countries}
        selected={values.countries}
        onChange={(next) => patch({ countries: next })}
      />
      <MultiSelectFilter
        label={t('pages.admin.providers.filters.currency')}
        options={named(options?.currencies)}
        selected={values.currencies}
        onChange={(currencies) => patch({ currencies })}
      />
      <MultiSelectFilter
        label={t('pages.admin.providers.filters.paymentMethod')}
        options={named(options?.payment_methods)}
        selected={values.paymentMethods}
        onChange={(paymentMethods) => patch({ paymentMethods })}
      />
      <MultiSelectFilter
        label={t('pages.admin.providers.filters.integration')}
        options={named(options?.integration_methods)}
        selected={values.integrations}
        onChange={(integrations) => patch({ integrations })}
      />
      <MultiSelectFilter
        label={t('pages.admin.providers.filters.industry')}
        options={named(options?.industries)}
        selected={values.industries}
        onChange={(industries) => patch({ industries })}
      />
      <SimpleSelect
        aria-label={t('pages.admin.providers.fields.status')}
        options={[
          { label: t('pages.admin.providers.allStatuses'), value: '' },
          ...statusOptions.map((status) => ({
            label: t(PROVIDER_STATUS_I18N_KEY[status]),
            value: status,
          })),
        ]}
        triggerClassName="h-9 rounded-xl"
        value={values.status}
        onValueChange={(status) =>
          patch({ status: status as AvailabilityStatus | '' })
        }
      />
    </div>
  );
};

ProviderFilters.displayName = 'ProviderFilters';
